import React, { useState } from 'react';
import {
    collection,
    addDoc,
    getDocs,
    doc,
    updateDoc,
    serverTimestamp,
    arrayUnion,
    Timestamp
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import { type JobStatus, type Job, type LossClassification } from '@/types/jobs';
import {
    getReportSortMs,
    stripLossClassificationReasoning,
} from '@/utils/stripLossClassificationReasoning';
import { type JobCreateFormState } from '@/pages/jobs/hooks/useJobCreateFormState';

interface UseJobCreateSubmitParams {
    form: JobCreateFormState;
    buildClassification: () => LossClassification | null;
    onClose: () => void;
    initialData?: Job;
    jobId?: string;
}

export const useJobCreateSubmit = ({
    form,
    buildClassification,
    onClose,
    initialData,
    jobId,
}: UseJobCreateSubmitParams) => {
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const {
        profile,
        officeId,
        departmentId,
        fnolReceivedDate,
        lossDate,
        lossCategory,
        lossDescription,
        notes,
        carrier,
        claimNumber,
        policyNumber,
        adjusterName,
        adjusterEmail,
        adjusterPhone,
        customerName,
        customerPhone,
        address,
        city,
        state,
        zip,
        county,
        assignments,
        classificationManuallyEdited,
        classificationReasoningChanges,
        storedClassificationSource,
    } = form;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);
        setLoading(true);

        try {
            const built = buildClassification();
            // Provenance (see ClassificationSource). A new job's category comes
            // only from this form's picker, so it is human. On an edit the form
            // is pre-filled from the stored value, which may be an AI reading:
            // it becomes human only when the person confirmed a change, and
            // otherwise keeps whatever source it already had (none stays none).
            const source = !jobId || classificationManuallyEdited ? 'human' : storedClassificationSource;
            const classification: LossClassification | null = built
                ? { ...built, ...(source ? { source } : {}) }
                : null;

            const commonData = {
                officeId,
                departmentId,
                // departmentIds handled in create/update blocks below

                fnol: {
                    receivedDate: fnolReceivedDate ? Timestamp.fromDate(new Date(fnolReceivedDate)) : null,
                    lossDate: lossDate ? Timestamp.fromDate(new Date(lossDate)) : null,
                    lossCategory,
                    lossDescription,
                    notes,

                    insurance: {
                        carrier,
                        claimNumber,
                        policyNumber,
                        adjuster: {
                            name: adjusterName,
                            email: adjusterEmail,
                            phone: adjusterPhone,
                        },
                    },

                    customer: {
                        name: customerName,
                        phone: customerPhone,
                        email: ''
                    },

                    property: {
                        address,
                        city,
                        state,
                        zip,
                        county
                    }
                },

                scopeVersion: 0,

                status: initialData?.status || 'PENDING' as JobStatus,

                assignments: assignments,
                assignedUserIds: [
                    assignments.supervisorId,
                    assignments.leadTechnicianId,
                    ...(assignments.teamMemberIds || [])
                ].filter(Boolean),

                // FNOL is the canonical nested source; root customer/property/etc. are no longer written on create/update.
            };

            if (jobId) {
                if (!profile?.orgId) throw new Error("Missing Org ID");
                const updatePayload: Record<string, unknown> = {
                    ...commonData,
                    departmentIds: arrayUnion(departmentId),
                    updatedAt: serverTimestamp(),
                };
                if (classification) {
                    updatePayload['claimData.classification'] = classification;
                }
                if (classificationManuallyEdited) {
                    const aiReportsRef = collection(db, 'organizations', profile.orgId, 'jobs', jobId, 'aiReports');
                    const aiReportsSnap = await getDocs(aiReportsRef);

                    let sourceDocData: Record<string, unknown> | null = null;
                    let sourceDocId: string | null = null;

                    if (!aiReportsSnap.empty) {
                        const latest = aiReportsSnap.docs
                            .map((reportDoc) => ({
                                id: reportDoc.id,
                                data: reportDoc.data() as Record<string, unknown>,
                            }))
                            .sort((a, b) => getReportSortMs(b.data) - getReportSortMs(a.data))[0];

                        sourceDocData = latest.data;
                        sourceDocId = latest.id;
                    } else if (initialData?.aiReports && typeof initialData.aiReports === 'object') {
                        sourceDocData = { aiReports: initialData.aiReports as Record<string, unknown> };
                    }

                    if (sourceDocData) {
                        const sanitized = stripLossClassificationReasoning(sourceDocData, classificationReasoningChanges);
                        await addDoc(aiReportsRef, {
                            ...sanitized,
                            sourceReportId: sourceDocId,
                            manualClassificationOverride: true,
                            overrideType: 'lossClassification',
                            createdAt: serverTimestamp(),
                            updatedAt: serverTimestamp(),
                        });
                    }
                }
                await updateDoc(doc(db, 'organizations', profile.orgId, 'jobs', jobId), updatePayload);
            } else {
                if (!profile?.orgId) throw new Error("Missing Org ID");
                await addDoc(collection(db, 'organizations', profile.orgId, 'jobs'), {
                    ...commonData,
                    ...(classification ? { claimData: { classification } } : {}),
                    departmentIds: [departmentId],
                    orgId: profile.orgId,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    status: 'PENDING'
                });
            }

            onClose();
        } catch (error) {
            const msg = error instanceof Error ? error.message : String(error);
            logger.error("Error creating/updating job:", msg);
            setSubmitError(`The job was not saved: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    return { handleSubmit, loading, submitError };
};
